import { ModalCustom } from "components/ModalCustom";
import { CustomDatePicker } from "components/DatePicker";
import { PrimaryButton } from "components/Button/style";
import { DropdownSelect } from "components/DropdownSelect";
import { Informacao } from "components/Informacao";
import FileUpload from "components/UploadArquivos";
import { Form } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaExclamationCircle,
  FaInfoCircle,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { Data } from "_utils/Data";
import { toastErro } from "components/Toast";
import { ISiglaDocumento } from "../../constants/siglasDocumento";
import {
  ArquivoLinha,
  ArquivoNome,
  CaixaCampos,
  Campo,
  CampoFlex,
  Descricao,
  ErroArquivo,
  ErrosResumo,
  ExemploBox,
  FooterBotoes,
  IconeRemoverPagamento,
  LabelDica,
  LinhaCampos,
  ListaArquivos,
  SiglasBusca,
  SiglasRef,
  SiglasTabelaWrapper,
  SiglasToggle,
} from "./style";

interface IProps {
  show: boolean;
  eTaxa: boolean;
  umProcessoTaxa: boolean;
  umProcessoFornecedor: boolean;
  idProcesso: number | null;
  selecaoMista: boolean;
  siglas: ISiglaDocumento[];
  erros: Array<{ arquivo: string; mensagem: string }>;
  handleClose: () => void;
  onConfirm: (
    dataPagamento: string,
    arquivos: File[],
    siglaComprovante: string,
  ) => void | Promise<void>;
  onRemoverErro?: (arquivo: string) => void;
}

export function ModalConfirmarPagamento({
  show,
  eTaxa,
  umProcessoTaxa,
  umProcessoFornecedor,
  idProcesso,
  selecaoMista,
  siglas,
  erros,
  handleClose,
  onConfirm,
  onRemoverErro,
}: IProps) {
  const dataPagamentoPadrao = () => {
    const data = new Date();
    data.setSeconds(0, 0);
    return data;
  };

  const [dataPagamento, setDataPagamento] = useState<Date | null>(
    dataPagamentoPadrao(),
  );
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [siglaComprovante, setSiglaComprovante] = useState("");
  const [mostrarSiglas, setMostrarSiglas] = useState(false);
  const [buscaSigla, setBuscaSigla] = useState("");
  const [processando, setProcessando] = useState(false);
  const [errosValidacao, setErrosValidacao] = useState<{ arquivo: string; mensagem: string }[]>([]);
  const contadorRef = useRef(0);
  const [pagamentos, setPagamentos] = useState<
    { id: number; sigla: string; arquivo: File | null }[]
  >([{ id: ++contadorRef.current, sigla: "", arquivo: null }]);

  useEffect(() => {
    if (!show) {
      setDataPagamento(dataPagamentoPadrao());
      setArquivos([]);
      setSiglaComprovante("");
      setMostrarSiglas(false);
      setBuscaSigla("");
      setProcessando(false);
      setErrosValidacao([]);
      setPagamentos([{ id: ++contadorRef.current, sigla: "", arquivo: null }]);
    }
  }, [show]);

  const setSiglaPagamento = (index: number, sigla: string) =>
    setPagamentos((prev) => prev.map((p, i) => (i === index ? { ...p, sigla } : p)));

  const setArquivoPagamento = (index: number, arquivo: File | null) =>
    setPagamentos((prev) => prev.map((p, i) => (i === index ? { ...p, arquivo } : p)));

  const adicionarPagamento = () =>
    setPagamentos((prev) => [
      ...prev,
      { id: ++contadorRef.current, sigla: "", arquivo: null },
    ]);

  const removerPagamento = (index: number) =>
    setPagamentos((prev) => prev.filter((_, i) => i !== index));

  const adicionarArquivos = (novos: File[]) => {
    setErrosValidacao([]);
    setArquivos((prev) => [...prev, ...novos]);
  };

  const selecionarComprovanteUnico = (arquivo: File) => {
    setErrosValidacao([]);
    setArquivos([arquivo]);
  };

  const limparArquivos = () => {
    setErrosValidacao([]);
    setArquivos([]);
  };

  const removerArquivo = (nome: string) => {
    setErrosValidacao((prev) => prev.filter((e) => e.arquivo !== nome));
    onRemoverErro?.(nome);
    setArquivos((prev) => {
      const indice = prev.findIndex((a) => a.name === nome);
      if (indice === -1) return prev;
      return prev.filter((_, i) => i !== indice);
    });
  };

  const validarArquivosTaxa = (): { arquivo: string; mensagem: string }[] => {
    const siglasValidas = new Set((siglas ?? []).map((s) => s.sigla.toUpperCase()));
    const erros: { arquivo: string; mensagem: string }[] = [];

    for (const arquivo of arquivos) {
      const match = arquivo.name.match(/^(\d{1,8})-([A-Za-z0-9]+)\.[^.]+$/);
      if (!match) {
        erros.push({
          arquivo: arquivo.name,
          mensagem: "Nome fora do padrão. Use NúmeroProcesso-Sigla.extensão (ex.: 400-CO.pdf)",
        });
        continue;
      }
      const sigla = match[2].toUpperCase();
      if (!siglasValidas.has(sigla)) {
        erros.push({ arquivo: arquivo.name, mensagem: `Sigla desconhecida: ${match[2]}` });
      }
    }

    return erros;
  };

  const handleConfirmTaxaProcesso = async () => {
    if (processando) return;
    if (!dataPagamento) {
      toastErro("Informe a data de pagamento");
      return;
    }
    if (pagamentos.some((p) => !p.sigla || !p.arquivo)) {
      toastErro("Selecione a taxa e o arquivo de cada pagamento");
      return;
    }
    if (!idProcesso) {
      toastErro("Não foi possível identificar o processo do lote");
      return;
    }

    const arquivosRenomeados = pagamentos.map((p) => {
      const ext = p.arquivo!.name.split(".").pop() ?? "pdf";
      const nome = `${idProcesso}-${p.sigla}.${ext}`;
      return new File([p.arquivo!], nome, { type: p.arquivo!.type });
    });

    try {
      setProcessando(true);
      await onConfirm(new Data(dataPagamento).getDataHoraEua() as string, arquivosRenomeados, "");
    } finally {
      setProcessando(false);
    }
  };

  const handleConfirm = async () => {
    if (umProcessoTaxa) {
      await handleConfirmTaxaProcesso();
      return;
    }
    if (selecaoMista || processando) {
      return;
    }
    if (!dataPagamento) {
      toastErro("Informe a data de pagamento");
      return;
    }
    if (!arquivos.length) {
      toastErro("Anexe ao menos um comprovante de pagamento");
      return;
    }
    if (umProcessoFornecedor && !siglaComprovante) {
      toastErro("Informe o tipo do arquivo do comprovante");
      return;
    }
    if (eTaxa) {
      const erros = validarArquivosTaxa();
      setErrosValidacao(erros);
      if (erros.length) {
        return;
      }
    }
    try {
      setProcessando(true);
      await onConfirm(
        new Data(dataPagamento).getDataHoraEua() as string,
        arquivos,
        siglaComprovante,
      );
    } finally {
      setProcessando(false);
    }
  };

  const siglasFiltradas = (siglas ?? []).filter(
    (s) =>
      s.documento.toLowerCase().includes(buscaSigla.toLowerCase()) ||
      s.sigla.toLowerCase().includes(buscaSigla.toLowerCase()),
  );

  const minDataPagamento = new Date();
  let diasUteisSubtraidos = 0;
  while (diasUteisSubtraidos < 2) {
    minDataPagamento.setDate(minDataPagamento.getDate() - 1);
    const diaSemana = minDataPagamento.getDay();
    if (diaSemana !== 0 && diaSemana !== 6) {
      diasUteisSubtraidos++;
    }
  }
  minDataPagamento.setHours(0, 0, 0, 0);

  return (
    <ModalCustom
      show={show}
      handleClose={handleClose}
      title="Confirmar pagamento"
      centered
      size={umProcessoTaxa || umProcessoFornecedor ? "lg" : undefined}
      footer
      footerTools={
        <FooterBotoes>
          <PrimaryButton outline variante="primary" onClick={handleClose}>
            Cancelar
          </PrimaryButton>
          <PrimaryButton onClick={handleConfirm} disabled={selecaoMista || processando}>
            Salvar
          </PrimaryButton>
        </FooterBotoes>
      }
    >
      {selecaoMista && (
        <Informacao
          mensagem={
            <span style={{ fontSize: "0.85rem" }}>
              Selecione apenas um tipo de pagamento para realizar a baixa (taxa
              ou fornecedor)
            </span>
          }
          type="atencao"
          showBotaoFechar={false}
          showLabel={false}
          icone={<FaExclamationCircle />}
          style={{ alignItems: "flex-start", marginBottom: 16 }}
        />
      )}
      <Descricao>
        Preencha as informações abaixo para confirmar o pagamento.
      </Descricao>

      <CaixaCampos
        style={
          selecaoMista ? { pointerEvents: "none", opacity: 0.6 } : undefined
        }
      >
        <Campo>
          <Form.Group>
            <Form.Label>Data e hora do pagamento</Form.Label>
            <CustomDatePicker
              selected={dataPagamento}
              onChange={(d) => {
                const data = d as Date | null;
                data?.setSeconds(0, 0);
                setDataPagamento(data);
              }}
              placeholder="Selecione a data e hora de pagamento"
              dateFormat="dd/MM/yyyy HH:mm:ss"
              minDate={minDataPagamento}
              showTimeSelect
            />
          </Form.Group>
        </Campo>

        {umProcessoFornecedor && (
          <Campo>
            <LinhaCampos>
              <CampoFlex>
                <Form.Label>
                  Tipo do arquivo{" "}
                  <LabelDica>(informe o tipo do documento)</LabelDica>
                </Form.Label>
                <DropdownSelect
                  options={(siglas ?? []).map((s) => ({
                    value: s.sigla,
                    label: s.documento,
                  }))}
                  title=""
                  selectedValue={siglaComprovante || null}
                  onSelect={(v) => setSiglaComprovante(v)}
                  searchable
                  required
                />
              </CampoFlex>
              <CampoFlex>
                <Form.Label>Comprovante de pagamento</Form.Label>
                <FileUpload
                  id="uploadComprovanteFornecedor"
                  textoSelecionado={arquivos[0]?.name}
                  onFileUpload={selecionarComprovanteUnico}
                  inputAccept=""
                />
              </CampoFlex>
            </LinhaCampos>
          </Campo>
        )}

        {!umProcessoTaxa && !umProcessoFornecedor && (
          <Campo>
            <Form.Group>
              <Form.Label>Comprovantes de pagamento</Form.Label>
              <FileUpload
                id="uploadComprovante"
                multiple
                disabled={selecaoMista}
                limparAoSelecionar
                onLimpar={limparArquivos}
                textoSelecionado={
                  arquivos.length === 1
                    ? arquivos[0].name
                    : arquivos.length > 1
                      ? `${arquivos.length} selecionados`
                      : undefined
                }
                onFileUpload={(file) => adicionarArquivos([file])}
                onFilesUpload={(files) => adicionarArquivos(files)}
                inputAccept=""
              />
            </Form.Group>
          </Campo>
        )}

        {umProcessoTaxa &&
          pagamentos.map((pagamento, index) => (
            <LinhaCampos key={pagamento.id}>
              <CampoFlex>
                <Form.Label>
                  Taxa <LabelDica>(informe o tipo de taxa)</LabelDica>
                </Form.Label>
                <DropdownSelect
                  options={(siglas ?? []).map((s) => ({
                    value: s.sigla,
                    label: s.documento,
                  }))}
                  title=""
                  selectedValue={pagamento.sigla || null}
                  onSelect={(v) => setSiglaPagamento(index, v)}
                  searchable
                  required
                />
              </CampoFlex>
              <CampoFlex>
                <Form.Label>Comprovante de pagamento</Form.Label>
                <FileUpload
                  id={`uploadComprovanteTaxa-${pagamento.id}`}
                  textoSelecionado={pagamento.arquivo?.name}
                  onFileUpload={(file) => setArquivoPagamento(index, file)}
                  inputAccept=""
                />
              </CampoFlex>
              {pagamentos.length > 1 && (
                <IconeRemoverPagamento
                  onClick={() => removerPagamento(index)}
                  title="Remover pagamento"
                />
              )}
            </LinhaCampos>
          ))}

        {umProcessoTaxa && (
          <button
            type="button"
            onClick={adicionarPagamento}
            style={{
              background: "none",
              border: "none",
              color: "#000",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: 0,
              marginTop: 16,
              fontSize: "0.9rem",
            }}
          >
            <FaPlus /> Adicionar outro pagamento
          </button>
        )}
      </CaixaCampos>

      {eTaxa && !umProcessoTaxa && (
        <ExemploBox>
          <FaInfoCircle />
          <div>
            <strong>Exemplo para renomeação de arquivo</strong>
            <div>NumeroProcesso-Sigla.pdf (400-CO.pdf)</div>
          </div>
        </ExemploBox>
      )}

      {eTaxa && !umProcessoTaxa && (
        <SiglasRef>
          <SiglasToggle
            type="button"
            onClick={() => setMostrarSiglas((v) => !v)}
          >
            Siglas {mostrarSiglas ? <FaChevronUp /> : <FaChevronDown />}
          </SiglasToggle>
          {mostrarSiglas && (
            <>
              <SiglasBusca
                placeholder="Procurar"
                value={buscaSigla}
                onChange={(e) => setBuscaSigla(e.target.value)}
              />
              <SiglasTabelaWrapper>
                <table>
                  <thead>
                    <tr>
                      <th>Documento</th>
                      <th>Sigla</th>
                    </tr>
                  </thead>
                  <tbody>
                    {siglasFiltradas.map((s, i) => (
                      <tr key={`${s.sigla}-${i}`}>
                        <td>{s.documento}</td>
                        <td>{s.sigla}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SiglasTabelaWrapper>
            </>
          )}
        </SiglasRef>
      )}

      {[...errosValidacao, ...erros].length > 0 && (
        <>
          <ListaArquivos>
            {[...errosValidacao, ...erros].map((e, i) => (
            <ArquivoLinha key={`${e.arquivo}-${i}`}>
              <div>
                <ArquivoNome>{e.arquivo}</ArquivoNome>
                <ErroArquivo>{e.mensagem}</ErroArquivo>
              </div>
              <FaTrash
                onClick={() => removerArquivo(e.arquivo)}
                title="Remover"
              />
            </ArquivoLinha>
            ))}
          </ListaArquivos>
        </>
      )}
    </ModalCustom>
  );
}
